import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { getStripeWebhookEvents, getStripeWebhookEvent, retryStripeWebhookEvent } from '../api/stripeApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { FilterBar, FilterSelect, FilterSelectOption } from '../components/FilterBar';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';

const { semantic } = colorTokens;

const STATUSES = ['received', 'processing', 'processed', 'failed', 'ignored', 'duplicate'];

const monoCellSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  maxWidth: 220,
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

const errorCellSx = {
  ...monoCellSx,
  maxWidth: 280,
  color: semantic.text.secondary,
};

function webhookStatusTone(status) {
  if (status === 'processed') return 'Success';
  if (status === 'failed') return 'Danger';
  if (status === 'ignored' || status === 'duplicate') return 'Neutral';
  if (status === 'processing') return 'Warning';
  if (status === 'received') return 'Info';
  return 'Neutral';
}

function TechText({ value, emptyValue, sx }) {
  if (!value) return emptyValue;
  const text = String(value);
  return (
    <Typography component="span" title={text} sx={sx || monoCellSx}>
      {text}
    </Typography>
  );
}

export function StripeWebhookEventsPage() {
  const { t } = useTranslation(['stripe', 'common']);
  const { session } = useAuth();
  const canRetry = hasPermission(session, 'system.stripe.webhooks.retry');

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = events.length;

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = { per_page: 100 };
      if (statusFilter) params.status = statusFilter;
      if (eventTypeFilter) params.event_type = eventTypeFilter;
      const response = await getStripeWebhookEvents(params);
      setEvents(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, eventTypeFilter]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const openDetail = useCallback(async (event) => {
    try {
      const data = await getStripeWebhookEvent(event.id);
      setDetail(data);
      setDetailOpen(true);
    } catch {
      setDetail(event);
      setDetailOpen(true);
    }
  }, []);

  const handleRetry = useCallback(async (event) => {
    setMessage('');
    try {
      await retryStripeWebhookEvent(event.id);
      setMessage(t('stripe:webhooks.retried', { eventId: event.stripe_event_id }));
      loadEvents();
    } catch (err) {
      setMessage(err?.response?.data?.message || t('stripe:webhooks.retry_failed'));
    }
  }, [loadEvents, t]);

  const columns = useMemo(
    () => [
      {
        id: 'stripe_event_id',
        label: t('stripe:webhooks.columns.stripe_event_id'),
        render: (row) => (
          <TechText value={row.stripe_event_id} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'event_type',
        label: t('stripe:webhooks.columns.event_type'),
        render: (row) => (
          <TechText value={row.event_type} emptyValue={emptyValue} />
        ),
      },
      {
        id: 'status',
        label: t('common:status'),
        render: (row) => (
          row.status
            ? (
              <StatusBadge
                status={row.status}
                category="generic"
                apiLabel={t(`stripe:webhook_statuses.${row.status}`, { defaultValue: row.status })}
                size="Sm"
                tone={webhookStatusTone(row.status)}
              />
            )
            : emptyValue
        ),
      },
      {
        id: 'processing_attempts',
        label: t('stripe:webhooks.columns.attempts'),
        render: (row) => (
          row.processing_attempts === 0 || row.processing_attempts
            ? row.processing_attempts
            : emptyValue
        ),
      },
      {
        id: 'received_at',
        label: t('stripe:webhooks.columns.received'),
        render: (row) => row.received_at || emptyValue,
      },
      {
        id: 'processed_at',
        label: t('stripe:webhooks.columns.processed'),
        render: (row) => row.processed_at || emptyValue,
      },
      {
        id: 'failure_reason',
        label: t('stripe:webhooks.columns.failure'),
        render: (row) => (
          <TechText value={row.failure_reason} emptyValue={emptyValue} sx={errorCellSx} />
        ),
      },
      {
        id: 'actions',
        label: t('common:actions'),
        align: 'right',
        render: (row) => (
          <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
            <Button
              variant="Ghost"
              onClick={() => openDetail(row)}
              sx={{ minHeight: 32, px: 1 }}
            >
              {t('common:view')}
            </Button>
            {canRetry && row.status === 'failed' && (
              <Button
                variant="Ghost"
                onClick={() => handleRetry(row)}
                sx={{
                  minHeight: 32,
                  px: 1,
                  color: semantic.error,
                  border: `1px solid ${semantic.error}`,
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: 'rgba(211, 47, 47, 0.08)',
                    borderColor: semantic.error,
                    color: semantic.error,
                  },
                }}
              >
                {t('common:retry')}
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    [canRetry, emptyValue, handleRetry, openDetail, t],
  );

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('stripe:webhooks.title')}
        subtitle={t('stripe:webhooks.subtitle')}
        actions={(
          <Button variant="Secondary" component={RouterLink} to="/stripe">
            {t('stripe:dashboard.title')}
          </Button>
        )}
      />

      {message && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadEvents} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('common:load_failed')}
        </Alert>
      )}

      <FilterBar type="Simple">
        <FilterSelect
          label={t('common:status')}
          hideLabel
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          minWidth={160}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          {STATUSES.map((status) => (
            <FilterSelectOption key={status} value={status}>
              {t(`stripe:webhook_statuses.${status}`, { defaultValue: status })}
            </FilterSelectOption>
          ))}
        </FilterSelect>
        <FilterSelect
          label={t('stripe:webhooks.event_type')}
          hideLabel
          value={eventTypeFilter}
          onChange={(e) => setEventTypeFilter(e.target.value)}
          minWidth={220}
        >
          <FilterSelectOption value="">{t('common:all')}</FilterSelectOption>
          <FilterSelectOption value="invoice.paid">
            {t('stripe:event_types.invoice.paid')}
          </FilterSelectOption>
          <FilterSelectOption value="invoice.payment_failed">
            {t('stripe:event_types.invoice.payment_failed')}
          </FilterSelectOption>
          <FilterSelectOption value="customer.subscription.updated">
            {t('stripe:event_types.customer.subscription.updated')}
          </FilterSelectOption>
        </FilterSelect>
      </FilterBar>

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('stripe:webhooks.events')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('stripe:webhooks.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={events}
            getRowKey={(row) => row.id}
            emptyMessage={t('common:no_data')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        )}
      </SectionCard>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: semantic.bg.surface,
            backgroundImage: 'none',
            border: `1px solid ${semantic.border.subtle}`,
            borderRadius: `${radiusTokens.card}px`,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
          }}
        >
          {t('stripe:webhooks.detail_title')}
        </DialogTitle>
        <DialogContent>
          <Box
            component="pre"
            sx={{
              ...monoCellSx,
              maxWidth: '100%',
              whiteSpace: 'pre-wrap',
              m: 0,
              color: semantic.text.secondary,
            }}
          >
            {JSON.stringify(detail?.payload || detail?.payload_preview || detail, null, 2)}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
